import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { IconLoader4, IconMail, IconMailOpened, IconRefresh } from '@tabler/icons-react';
import { usePasswordReset } from '../hooks/use-password-reset';

interface ForgotPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Starts a reset from the sign-in form. The shopper finishes it on the
 * /forgot-password page, which the emailed link opens.
 */
type Step = 'contact' | 'sent';

const RESEND_COOLDOWN = 60;

export const ForgotPasswordModal = ({ open, onOpenChange }: ForgotPasswordModalProps) => {
  const { requestReset } = usePasswordReset();

  const [step, setStep] = useState<Step>('contact');
  const [contactInfo, setContactInfo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setStep('contact');
      setContactInfo('');
      setError(null);
      setResendCooldown(0);
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    }
  }, [open]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const email = contactInfo.trim();
    if (!email) {
      setError('Please enter the email address on your account');
      return;
    }
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await requestReset.mutateAsync(email);
      startCooldown();
      setStep('sent');
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to send a reset link. Please check the email address and try again.',
      );
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || requestReset.isPending) return;
    setError(null);
    try {
      await requestReset.mutateAsync(contactInfo.trim());
      startCooldown();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to resend the reset link.');
    }
  };

  const config =
    step === 'contact'
      ? {
          icon: IconMail,
          title: 'Find Your Account',
          description: "Enter the email address on your account and we'll email you a link to reset your password.",
        }
      : {
          icon: IconMailOpened,
          title: 'Check Your Inbox',
          description: `If an account exists for ${contactInfo.trim()}, we sent it a password reset link. The link expires in 15 minutes. Check your spam folder if it doesn't arrive.`,
        };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <config.icon className="h-5 w-5" />
            {config.title}
          </DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step: Contact */}
        {step === 'contact' && (
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <div className="relative">
                <IconMail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reset-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  placeholder="you@example.com"
                  value={contactInfo}
                  onChange={(e) => setContactInfo(e.target.value)}
                  className="pl-10"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={requestReset.isPending || !contactInfo.trim()} className="flex-1">
                {requestReset.isPending ? (
                  <>
                    <IconLoader4 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </div>
          </form>
        )}

        {/* Step: Link sent */}
        {step === 'sent' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Didn't get the email?</p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || requestReset.isPending}
                className="flex items-center gap-1.5 text-sm font-medium text-primary disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
              >
                <IconRefresh className="h-3 w-3" />
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Link'}
              </button>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setStep('contact');
                  setError(null);
                }}
              >
                Back
              </Button>
              <Button type="button" className="flex-1" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
